import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FaCalendarAlt, FaUsers, FaPlus, FaCheck, FaTimes, FaPaperPlane, FaExclamationTriangle, FaClock, FaEdit, FaTrashAlt, FaArchive, FaUndo, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { toast } from 'sonner';
import { DateInput } from '../ui/DateInput';
import { FaListUl } from 'react-icons/fa';
import { TemplatePickerModal, appliedMessage } from '../events/templates/TemplatePickerModal';

const API = '/api';

export function EventsTab({
  events,
  setEvents,
  familyId,
  showForm,
  setShowForm,
  onEventCreated,
  hasPermission,
  isOwner,
  currentUser,
  ancestralColors,
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    location: '',
    description: '',
  });
  const [creating, setCreating] = useState(false);
  const [submittedNotice, setSubmittedNotice] = useState(null); // string: name of event submitted
  const [rejectTarget, setRejectTarget] = useState(null); // event being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editData, setEditData] = useState({ name: '', start_date: '', end_date: '', location: '', description: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [templateEvent, setTemplateEvent] = useState(null);

  const canApprove = isOwner || (hasPermission && hasPermission('manage_events'));
  const userId = currentUser?.user_id;

  const canEditEvent = (event) => event.created_by === userId || isOwner;
  const canManageEvent = (event) => event.created_by === userId || canApprove;

  // Categorize events. Treat missing status as approved (backward compat).
  const { approved, pending, myDrafts, archived } = useMemo(() => {
    const a = []; const p = []; const d = []; const ar = [];
    (events || []).forEach((e) => {
      const status = e.status || 'approved';
      if (status === 'pending_approval') p.push(e);
      else if (status === 'draft' && e.created_by === userId) d.push(e);
      else if (e.is_archived) ar.push(e);
      else a.push(e);
    });
    return { approved: a, pending: p, myDrafts: d, archived: ar };
  }, [events, userId]);

  const replaceEvent = (updated) => {
    if (!setEvents) return;
    setEvents((prev) => prev.map((e) => (e.event_id === updated.event_id ? updated : e)));
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'TBD';
    const start = new Date(startDate).toLocaleDateString();
    if (!endDate || endDate === startDate) return start;
    const end = new Date(endDate).toLocaleDateString();
    return `${start} - ${end}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/events`,
        { ...formData, family_id: familyId },
        { withCredentials: true }
      );
      const created = response.data;
      onEventCreated(created);
      setShowForm(false);
      const name = formData.name;
      setFormData({ name: '', start_date: '', end_date: '', location: '', description: '' });
      setTemplateEvent(created);
      if (created.status === 'pending_approval') {
        setSubmittedNotice(name);
        setTimeout(() => setSubmittedNotice(null), 8000);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert(error?.response?.data?.detail || 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (event) => {
    setBusyId(event.event_id);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/approve`, {}, { withCredentials: true });
      replaceEvent(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to approve event');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.event_id);
    try {
      const res = await axios.post(
        `${API}/events/${rejectTarget.event_id}/reject`,
        { reason: rejectReason },
        { withCredentials: true }
      );
      replaceEvent(res.data);
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to reject event');
    } finally {
      setBusyId(null);
    }
  };

  const handleResubmit = async (event) => {
    setBusyId(event.event_id);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/resubmit`, {}, { withCredentials: true });
      replaceEvent(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to resubmit event');
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (event) => {
    setEditData({
      name: event.name || '',
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      location: event.location || '',
      description: event.description || '',
    });
    setEditTarget(event);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      const res = await axios.patch(`${API}/events/${editTarget.event_id}`, editData, { withCredentials: true });
      replaceEvent(res.data);
      setEditTarget(null);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update event');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleArchive = async (event) => {
    setBusyId(event.event_id);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/archive`, {}, { withCredentials: true });
      replaceEvent(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to archive event');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnarchive = async (event) => {
    setBusyId(event.event_id);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/unarchive`, {}, { withCredentials: true });
      replaceEvent(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to restore event');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.event_id);
    try {
      await axios.delete(`${API}/events/${deleteTarget.event_id}`, { withCredentials: true });
      if (setEvents) setEvents((prev) => prev.filter((e) => e.event_id !== deleteTarget.event_id));
      setDeleteTarget(null);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete event');
    } finally {
      setBusyId(null);
    }
  };

  const primaryStyle = ancestralColors
    ? { background: `linear-gradient(135deg, ${ancestralColors.primary} 0%, ${ancestralColors.secondary} 100%)` }
    : { backgroundColor: '#2563EB' };

  return (
    <div data-testid="events-tab" className="space-y-6">
      {/* Actions Section — open to ALL members; non-approvers will submit for approval */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Event Actions</h4>
            <p className="text-sm text-gray-500 mt-1">
              {canApprove
                ? 'Create events directly, or review submissions from family members.'
                : "Anyone can propose an event. Yours will be sent to the family owner or Circle Steward for approval."}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-6 py-3 text-white rounded-lg hover:opacity-90 font-semibold transition-all shadow-md hover:shadow-lg whitespace-nowrap"
            style={primaryStyle}
            data-testid="create-event-btn"
          >
            <FaPlus />
            <span>{canApprove ? 'Create New Event' : 'Propose an Event'}</span>
          </button>
        </div>

        {templateEvent && (
          <TemplatePickerModal
            event={templateEvent}
            familyId={familyId}
            reopened={!!templateEvent.reopened}
            onClose={() => setTemplateEvent(null)}
            onApplied={(data) => {
              const ev = templateEvent;
              setTemplateEvent(null);
              toast.success(appliedMessage(data, ev.name), {
                action: { label: 'View tasks', onClick: () => navigate(`/event/${ev.event_id}`) },
              });
            }}
          />
        )}

        {submittedNotice && (
          <div
            className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3"
            data-testid="pending-submission-notice"
          >
            <FaPaperPlane className="mt-1 flex-shrink-0" />
            <div className="text-sm">
              <strong>“{submittedNotice}”</strong> was submitted for approval. The family owner and Circle Stewards
              have been notified. You'll get a notification once it's reviewed.
            </div>
          </div>
        )}
      </div>

      {/* Pending Approvals — approvers only */}
      {canApprove && pending.length > 0 && (
        <div
          className="bg-white rounded-xl border border-amber-200 p-6"
          data-testid="pending-approvals-section"
        >
          <div className="flex items-center gap-2 mb-4">
            <FaClock className="text-amber-600" />
            <h4 className="text-lg font-semibold text-gray-800">
              Pending Your Approval ({pending.length})
            </h4>
          </div>
          <div className="space-y-3">
            {pending.map((event) => (
              <div
                key={event.event_id}
                className="bg-amber-50 border border-amber-200 rounded-lg p-4"
                data-testid={`pending-event-${event.event_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                        Submitted by {event.created_by_name || 'a family member'}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{event.name}</h3>
                    <div className="mt-1 text-sm text-gray-700 space-y-1">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-gray-500" />
                        <span>{formatDateRange(event.start_date, event.end_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaUsers className="text-gray-500" />
                        <span>{event.location}</span>
                      </div>
                      {event.description && (
                        <p className="text-gray-600 mt-1">{event.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 md:flex-col md:w-40">
                    <button
                      onClick={() => handleApprove(event)}
                      disabled={busyId === event.event_id}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                      data-testid={`approve-event-${event.event_id}-btn`}
                    >
                      <FaCheck /> Approve
                    </button>
                    <button
                      onClick={() => { setRejectTarget(event); setRejectReason(''); }}
                      disabled={busyId === event.event_id}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 transition"
                      data-testid={`reject-event-${event.event_id}-btn`}
                    >
                      <FaTimes /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Drafts — events the current user submitted that were sent back for revision */}
      {myDrafts.length > 0 && (
        <div
          className="bg-white rounded-xl border border-orange-200 p-6"
          data-testid="my-drafts-section"
        >
          <div className="flex items-center gap-2 mb-4">
            <FaExclamationTriangle className="text-orange-500" />
            <h4 className="text-lg font-semibold text-gray-800">
              Your Drafts — Needs Revision ({myDrafts.length})
            </h4>
          </div>
          <div className="space-y-3">
            {myDrafts.map((event) => (
              <div
                key={event.event_id}
                className="bg-orange-50 border border-orange-200 rounded-lg p-4"
                data-testid={`draft-event-${event.event_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800">{event.name}</h3>
                    <div className="mt-1 text-sm text-gray-700 space-y-1">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-gray-500" />
                        <span>{formatDateRange(event.start_date, event.end_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaUsers className="text-gray-500" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                    {event.rejection_reason ? (
                      <div className="mt-2 bg-white border border-orange-200 rounded p-2 text-sm">
                        <span className="font-semibold text-orange-700">
                          Note from {event.rejected_by_name || 'reviewer'}:
                        </span>{' '}
                        <span className="text-gray-700">{event.rejection_reason}</span>
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-gray-600 italic">
                        Sent back for revision (no reason provided).
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 md:flex-col md:w-44">
                    <button
                      onClick={() => navigate(`/event/${event.event_id}`)}
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      data-testid={`edit-draft-${event.event_id}-btn`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleResubmit(event)}
                      disabled={busyId === event.event_id}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition"
                      style={primaryStyle}
                      data-testid={`resubmit-draft-${event.event_id}-btn`}
                    >
                      <FaPaperPlane /> Resubmit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved Events List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Family Events</h4>
        {approved.length === 0 ? (
          <div className="text-center py-8">
            <FaCalendarAlt className="text-gray-300 text-6xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No events yet</h3>
            <p className="text-gray-600">Create your first reunion or gathering!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {approved.map((event) => {
              const showEdit = canEditEvent(event);
              const showManage = canManageEvent(event);
              return (
              <div
                key={event.event_id}
                className="bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition p-6 cursor-pointer flex flex-col"
                onClick={() => navigate(`/event/${event.event_id}`)}
                data-testid={`event-card-${event.event_id}`}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-2">{event.name}</h3>
                <div className="space-y-2 text-gray-600">
                  <div className="flex items-center space-x-2">
                    <FaCalendarAlt />
                    <span>{formatDateRange(event.start_date || event.date, event.end_date)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaUsers />
                    <span>{event.location}</span>
                  </div>
                </div>
                {event.description && (
                  <p className="mt-3 text-gray-600 text-sm">{event.description}</p>
                )}
                {(showEdit || showManage) && (
                  <div
                    className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {showEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(event); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        data-testid={`edit-event-${event.event_id}-btn`}
                      >
                        <FaEdit /> Edit
                      </button>
                    )}
                    {showManage && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setTemplateEvent({ ...event, reopened: true }); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition"
                        data-testid={`template-event-${event.event_id}-btn`}
                      >
                        <FaListUl /> Task Templates
                      </button>
                    )}
                    {showManage && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleArchive(event); }}
                        disabled={busyId === event.event_id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-50 transition"
                        data-testid={`archive-event-${event.event_id}-btn`}
                      >
                        <FaArchive /> Archive
                      </button>
                    )}
                    {showManage && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(event); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition"
                        data-testid={`delete-event-${event.event_id}-btn`}
                      >
                        <FaTrashAlt /> Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Archived Events — hidden from active list, history & photos preserved */}
      {archived.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="archived-events-section">
          <button
            onClick={() => setShowArchived((s) => !s)}
            className="flex items-center gap-2 text-lg font-semibold text-gray-700 hover:text-gray-900 transition"
            data-testid="toggle-archived-btn"
          >
            {showArchived ? <FaChevronDown /> : <FaChevronRight />}
            <FaArchive className="text-gray-400" />
            <span>Archived Events ({archived.length})</span>
          </button>
          {showArchived && (
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              {archived.map((event) => {
                const showManage = canManageEvent(event);
                return (
                  <div
                    key={event.event_id}
                    className="bg-gray-100 rounded-xl border border-gray-200 p-6 flex flex-col"
                    data-testid={`archived-event-card-${event.event_id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-200 px-2 py-0.5 rounded">Archived</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-700 mb-2">{event.name}</h3>
                    <div className="space-y-1 text-sm text-gray-500">
                      <div className="flex items-center space-x-2"><FaCalendarAlt /><span>{formatDateRange(event.start_date || event.date, event.end_date)}</span></div>
                      <div className="flex items-center space-x-2"><FaUsers /><span>{event.location}</span></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/event/${event.event_id}`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        data-testid={`view-archived-${event.event_id}-btn`}
                      >
                        View
                      </button>
                      {showManage && (
                        <button
                          onClick={() => handleUnarchive(event)}
                          disabled={busyId === event.event_id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50 transition"
                          data-testid={`unarchive-event-${event.event_id}-btn`}
                        >
                          <FaUndo /> Restore
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create Event Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="create-event-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-1">
              {canApprove ? 'Create New Event' : 'Propose an Event'}
            </h3>
            {!canApprove && (
              <p className="text-sm text-gray-500 mb-3">
                Your submission will be sent to the family owner or a Circle Steward for approval.
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Event Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="event-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Start Date *</label>
                  <DateInput
                    value={formData.start_date}
                    onChange={(val) => setFormData({ ...formData, start_date: val })}
                    required
                    className="border-gray-300 focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent"
                    data-testid="event-start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    End Date <span className="text-gray-400 text-sm">(optional)</span>
                  </label>
                  <DateInput
                    value={formData.end_date}
                    onChange={(val) => setFormData({ ...formData, end_date: val })}
                    className="border-gray-300 focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent"
                    data-testid="event-end-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="event-location-input"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  rows="3"
                  data-testid="event-description-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all"
                  style={primaryStyle}
                  data-testid="submit-event-btn"
                >
                  {creating
                    ? (canApprove ? 'Creating...' : 'Submitting...')
                    : (canApprove ? 'Create Event' : 'Submit for Approval')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            data-testid="reject-event-modal"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-1">Send back for revision</h3>
            <p className="text-sm text-gray-500 mb-4">
              “{rejectTarget.name}” will be returned to {rejectTarget.created_by_name || 'the creator'} as a draft. Add a short note so they know what to change.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Please pick a weekend that doesn't clash with the family reunion."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              data-testid="reject-reason-input"
            />
            <div className="flex space-x-3 mt-4">
              <button
                type="button"
                onClick={() => { setRejectTarget(null); setRejectReason(''); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                data-testid="cancel-reject-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={busyId === rejectTarget.event_id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-reject-btn"
              >
                {busyId === rejectTarget.event_id ? 'Sending...' : 'Send Back'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {editTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="edit-event-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Edit Event</h3>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Event Name</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="edit-event-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Start Date *</label>
                  <DateInput
                    value={editData.start_date}
                    onChange={(val) => setEditData({ ...editData, start_date: val })}
                    required
                    className="border-gray-300 focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent"
                    data-testid="edit-event-start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    End Date <span className="text-gray-400 text-sm">(optional)</span>
                  </label>
                  <DateInput
                    value={editData.end_date}
                    onChange={(val) => setEditData({ ...editData, end_date: val })}
                    className="border-gray-300 focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent"
                    data-testid="edit-event-end-date-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Location</label>
                <input
                  type="text"
                  value={editData.location}
                  onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="edit-event-location-input"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Description</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  rows="3"
                  data-testid="edit-event-description-input"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all"
                  style={primaryStyle}
                  data-testid="save-event-edit-btn"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="delete-event-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Delete Event?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone. All tasks, RSVPs, and photos for this event will be permanently deleted.
              <span className="block mt-2 text-sm text-gray-500">
                Tip: choose <strong>Archive</strong> instead if you want to keep the history and photos.
              </span>
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                data-testid="cancel-delete-event-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={busyId === deleteTarget.event_id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-delete-event-btn"
              >
                {busyId === deleteTarget.event_id ? 'Deleting...' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
